import React, { useState } from "react";
import { Button, Form, Modal, Table } from "react-bootstrap";
import { AppCheckbox, AppDateField } from "./form";
import { authFetch } from "../api/authFetch";

const API = "/api/workshop-payroll";

/** Відображення ролі працівника (зберігається як seamstress / cutter / hardware / other). */
export function workerRoleLabelUk(role) {
    switch (role) {
        case "seamstress":
            return "Швачка";
        case "cutter":
            return "Закрійниця";
        case "hardware":
            return "Фурнітурниця";
        case "other":
            return "Інше";
        default:
            return role ? String(role) : "—";
    }
}

/** YYYY-MM-DD → DD.MM.YYYY */
function birthDateLabel(v) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v || ""));
    return m ? `${m[3]}.${m[2]}.${m[1]}` : "—";
}

/**
 * Працівники цеху: таблиця + модалка створення / редагування (спільна для «Цехи» та «Користувачі»).
 * `onChanged` — перезавантажити список цехів після збереження / видалення.
 */
export default function WorkshopWorkersCrud({ workshopId, workers, onChanged, title = "Працівники (CRUD)" }) {
    const [workerModal, setWorkerModal] = useState(null);
    const workersList = Array.isArray(workers) ? workers : [];

    const saveWorkerModal = async () => {
        if (!workshopId || !workerModal) return;
        const wf = workerModal;
        const role = ["seamstress", "cutter", "hardware", "other"].includes(wf.role) ? wf.role : "seamstress";
        let dailyRateUAH = null;
        if (role === "hardware") {
            const raw = wf.dailyRateUAH;
            const n =
                raw !== undefined && raw !== null && String(raw).trim() !== "" ? Number(String(raw).replace(",", ".")) : NaN;
            dailyRateUAH = Number.isFinite(n) && n >= 0 ? n : null;
        }
        const body = {
            fullName: String(wf.fullName || "").trim() || "Працівник",
            phone: String(wf.phone || ""),
            birthDate: String(wf.birthDate || ""),
            recipientName: String(wf.recipientName || ""),
            iban: String(wf.iban || ""),
            ipn: String(wf.ipn || ""),
            role,
            dailyRateUAH,
            notes: String(wf.notes || ""),
            active: wf.active !== false,
        };
        const r = await authFetch(
            wf._id ? `${API}/workshops/${workshopId}/workers/${wf._id}` : `${API}/workshops/${workshopId}/workers`,
            {
                method: wf._id ? "PATCH" : "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            }
        );
        if (!r.ok) {
            const err = await r.json().catch(() => ({}));
            alert(err.error || "Помилка");
            return;
        }
        setWorkerModal(null);
        await onChanged?.();
    };

    const deleteWorker = async (id) => {
        if (!window.confirm("Видалити працівника зі списку?")) return;
        const r = await authFetch(`${API}/workshops/${workshopId}/workers/${id}`, {
            method: "DELETE",
            credentials: "include",
        });
        if (!r.ok) {
            const err = await r.json().catch(() => ({}));
            alert(err.error || "Помилка");
            return;
        }
        await onChanged?.();
    };

    return (
        <>
            <h6 className="small text-uppercase text-muted mb-2">{title}</h6>
            <Table size="sm" bordered responsive className="bg-white mb-2">
                <thead className="table-light">
                    <tr>
                        <th>ПІБ</th>
                        <th>Телефон</th>
                        <th>Дата народження</th>
                        <th>Роль</th>
                        <th>Грн/день</th>
                        <th>Активний</th>
                        <th style={{ width: 140 }}>Дії</th>
                    </tr>
                </thead>
                <tbody>
                    {workersList.map((w) => (
                        <tr key={w._id}>
                            <td>{w.fullName}</td>
                            <td>{w.phone}</td>
                            <td>{birthDateLabel(w.birthDate)}</td>
                            <td>{workerRoleLabelUk(w.role)}</td>
                            <td className="small">
                                {w.role === "hardware" && w.dailyRateUAH != null ? `${w.dailyRateUAH} грн` : "—"}
                            </td>
                            <td>{w.active !== false ? "так" : "ні"}</td>
                            <td>
                                <Button
                                    size="sm"
                                    variant="outline-primary"
                                    className="me-1 py-0"
                                    onClick={() => setWorkerModal({ ...w })}
                                >
                                    Змінити
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline-danger"
                                    className="py-0"
                                    onClick={() => void deleteWorker(w._id)}
                                >
                                    Видалити
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            <Button
                size="sm"
                variant="outline-secondary"
                className="mb-3"
                onClick={() =>
                    setWorkerModal({
                        _id: null,
                        fullName: "",
                        phone: "",
                        birthDate: "",
                        recipientName: "",
                        iban: "",
                        ipn: "",
                        role: "seamstress",
                        dailyRateUAH: "",
                        notes: "",
                        active: true,
                    })
                }
            >
                + Додати працівника
            </Button>

            <Modal show={!!workerModal} onHide={() => setWorkerModal(null)} centered size="lg" className="workshop-payroll-module">
                <Modal.Header closeButton>
                    <Modal.Title>{workerModal?._id ? "Працівник" : "Новий працівник"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {workerModal && (
                        <div className="row g-2">
                            <div className="col-md-6">
                                <Form.Label className="small">ПІБ</Form.Label>
                                <Form.Control
                                    value={workerModal.fullName || ""}
                                    onChange={(e) => setWorkerModal((x) => ({ ...x, fullName: e.target.value }))}
                                />
                            </div>
                            <div className="col-md-6">
                                <Form.Label className="small">Телефон</Form.Label>
                                <Form.Control
                                    value={workerModal.phone || ""}
                                    onChange={(e) => setWorkerModal((x) => ({ ...x, phone: e.target.value }))}
                                />
                            </div>
                            <div className="col-md-6">
                                <Form.Label className="small">Роль</Form.Label>
                                <Form.Select
                                    value={workerModal.role || "seamstress"}
                                    onChange={(e) =>
                                        setWorkerModal((x) => ({
                                            ...x,
                                            role: e.target.value,
                                            ...(e.target.value !== "hardware" ? { dailyRateUAH: "" } : {}),
                                        }))
                                    }
                                >
                                    <option value="seamstress">Швачка</option>
                                    <option value="cutter">Закрійниця</option>
                                    <option value="hardware">Фурнітурниця</option>
                                    <option value="other">Інше</option>
                                </Form.Select>
                            </div>
                            <div className="col-md-6">
                                <Form.Label className="small">Дата народження</Form.Label>
                                <AppDateField
                                    value={workerModal.birthDate || ""}
                                    onChange={(v) => setWorkerModal((x) => ({ ...x, birthDate: v }))}
                                />
                            </div>
                            {workerModal.role === "hardware" && (
                                <div className="col-md-6">
                                    <Form.Label className="small">Ставка за день (грн)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min={0}
                                        step={1}
                                        placeholder="напр. 600"
                                        value={
                                            workerModal.dailyRateUAH != null && workerModal.dailyRateUAH !== ""
                                                ? workerModal.dailyRateUAH
                                                : ""
                                        }
                                        onChange={(e) =>
                                            setWorkerModal((x) => ({ ...x, dailyRateUAH: e.target.value }))
                                        }
                                    />
                                </div>
                            )}
                            <div className="col-md-6 d-flex align-items-end">
                                <AppCheckbox
                                    id={`wp-worker-active-${workshopId}`}
                                    label="Активний"
                                    checked={workerModal.active !== false}
                                    onChange={(e) => setWorkerModal((x) => ({ ...x, active: e.target.checked }))}
                                />
                            </div>
                            <div className="col-12">
                                <Form.Label className="small">ПІБ одержувача (реквізити)</Form.Label>
                                <Form.Control
                                    value={workerModal.recipientName || ""}
                                    onChange={(e) => setWorkerModal((x) => ({ ...x, recipientName: e.target.value }))}
                                />
                            </div>
                            <div className="col-md-6">
                                <Form.Label className="small">IBAN</Form.Label>
                                <Form.Control
                                    value={workerModal.iban || ""}
                                    onChange={(e) => setWorkerModal((x) => ({ ...x, iban: e.target.value }))}
                                />
                            </div>
                            <div className="col-md-6">
                                <Form.Label className="small">ІПН</Form.Label>
                                <Form.Control
                                    value={workerModal.ipn || ""}
                                    onChange={(e) => setWorkerModal((x) => ({ ...x, ipn: e.target.value }))}
                                />
                            </div>
                            <div className="col-12">
                                <Form.Label className="small">Нотатка</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={workerModal.notes || ""}
                                    onChange={(e) => setWorkerModal((x) => ({ ...x, notes: e.target.value }))}
                                />
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setWorkerModal(null)}>
                        Скасувати
                    </Button>
                    <Button size="sm" onClick={() => void saveWorkerModal()}>
                        Зберегти
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
